import { isContributionPast, warsawTodayIso } from './rosterInvoices';

/**
 * «Взнос просрочен» — ОДНО правило для чипа «Просроченные взносы» карточки
 * студента, плитки «Требует внимания» группы и счётчика просрочек на
 * вкладке «Финансы» группы. Перепроверка E1-2.
 *
 * ТЗ 0036: долг — это остаток уже наступивших взносов (дата < сегодня по
 * Варшаве), и выставленная фактура для этого НЕ нужна. Чип раньше считал
 * `status === 'overdue'` — а это статус «фактура выставлена и не оплачена в
 * срок»; взнос без фактуры живёт в `awaiting` хоть 95 дней. Студент с долгом
 * 6500 и 95 днями просрочки чипа не получал, хотя KPI «Долг» над ним горел.
 *
 * Правило — про ВЗНОС. Какие договоры вообще несут долг, решают
 * вызывающие, и одинаково (перепроверка G1): расторгнутый, бесплатный и
 * префинансируемый государством договоры не считаются — так их пропускают
 * KPI «Долг» (`buildStudentMoney`), группа (`buildMoneyModel`) и чип
 * (`useStudentAlerts`).
 */
export interface IOverdueCandidate {
  /** Срок взноса, ISO `YYYY-MM-DD`. Без даты — не просрочен. */
  date?: string | null;
  /** Остаток в валюте договора: `max(0, план − покрытие)` (сервер). */
  debt?: number | string | null;
  /** Тот же остаток под старым именем — если `debt` не выбран. */
  remainder?: number | string | null;
  /** Покрыт полностью — последний запасной признак. */
  complete?: boolean | null;
}

/** Меньше копейки — не долг (округление покрытия). */
const DEBT_EPSILON = 0.005;

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '';

const toNumber = (value: number | string | null | undefined): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

/** На взносе ещё есть долг: `debt`, иначе `remainder`, иначе `!complete`. */
export const contributionUnpaid = (c: IOverdueCandidate): boolean => {
  if (hasValue(c.debt)) return toNumber(c.debt) > DEBT_EPSILON;
  if (hasValue(c.remainder)) return toNumber(c.remainder) > DEBT_EPSILON;
  return !c.complete;
};

/**
 * Просрочен: срок прошёл (по Варшаве, `today` — `YYYY-MM-DD`) и долг
 * остался. `today` параметром — функция чистая; по умолчанию сегодня.
 */
export const isContributionOverdue = (
  c: IOverdueCandidate,
  today: string = warsawTodayIso(),
): boolean => isContributionPast(c.date, today) && contributionUnpaid(c);
